import { DailyRewardModalTracker } from "@components/features/DailyRewardModal/utils/DailyRewardModalTracker";
import { FUNDING_PROFILE_INFO_TOP_BAR_ID, TOAST_CONTAINER_ID, TOAST_OFFSET } from "@constants/ui";
import { GameIframeStateTracker } from "./GameIframeStateTracker";

/**
 * Calculates the additional margin-top (in px) that a toast needs (relative to the
 * toast container) so that it appears immediately below the funding profile top bar
 * plus a small extra offset. If the funding profile top bar is not present or not visible, returns 0.
 */
export function getToastOffset({
  toastContainerId = TOAST_CONTAINER_ID,
  fundingProfileTopBarId = FUNDING_PROFILE_INFO_TOP_BAR_ID,
  offset = TOAST_OFFSET,
}: {
  toastContainerId?: string;
  fundingProfileTopBarId?: string;
  offset?: number;
}) {
  const topBar = document.getElementById(fundingProfileTopBarId);
  if (!topBar) return 0;

  // Check if the top bar is actually visible (not just present in DOM)
  const computedStyle = window.getComputedStyle(topBar);
  const isVisible = computedStyle.opacity !== "0" && topBar.offsetHeight > 0;
  if (!isVisible) return 0;

  // Prefer querying by configured id; fallback to a well-known class on the container
  const toastContainer =
    document.getElementById(toastContainerId) || (document.querySelector(`.${toastContainerId}`) as HTMLElement | null);
  if (!toastContainer) return 0;

  const topBarRect = topBar.getBoundingClientRect();
  const containerRect = toastContainer.getBoundingClientRect();

  // Desired top edge of the toast content: just below the top bar + extra offset
  const desiredTopWithinViewport = topBarRect.bottom + offset;
  // Convert desired viewport position to a margin relative to the container's top
  const marginRelativeToContainer = desiredTopWithinViewport - containerRect.top;

  return Math.max(0, Math.round(marginRelativeToContainer));
}

/**
 * Checks if a game iframe dialog is currently open.
 * Uses event-based state tracking instead of DOM queries to avoid forced layout and improve INP.
 * @returns boolean indicating if a game iframe dialog is currently open
 */
export function isGameIframeDialogOpen(): boolean {
  return GameIframeStateTracker.getInstance().isOpen();
}

/**
 * Checks if the daily reward modal is pending open or currently open.
 * Uses event-based state tracking instead of DOM queries for better performance and reliability.
 * Includes "pending" state to prevent race conditions where the modal has been requested
 * but hasn't rendered yet (e.g., post-login drawer vs daily reward modal).
 */
export function isDailyRewardModalOpen(): boolean {
  return DailyRewardModalTracker.getInstance().isPendingOrOpen();
}
